import { Component, Input, ChangeDetectorRef, AfterViewInit, OnInit, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { round, trimEnd } from 'lodash';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';

@Component({
  selector: 'number-spinner',
  templateUrl: './number-spinner.component.html',
  styleUrls: ['./number-spinner.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class NumberSpinnerComponent implements OnInit, AfterViewInit {
  private DEFAULT_MIN = 0;
  private DEFAULT_MAX = 999;
  private DEFAULT_STEP = 1;
  private DEFAULT_INITIAL = 1;
  private DEFAULT_PRECISION = 0;
  private numberOrEmptyString = /^-?\d*(?:\.\d*)?$/;
  private notifyParent: boolean = false;

  initialValue: number = this.DEFAULT_INITIAL;
  minValue: number = this.DEFAULT_MIN;
  maxValue: number = this.DEFAULT_MAX;
  stepAmount: number = this.DEFAULT_STEP;
  currentValue: number | null = this.initialValue;

  @Input() floatPrecision: number = this.DEFAULT_PRECISION;
  @Input() appendPercent: boolean = false;
  @Input() isInputDisabled: boolean = true;
  @Output() valueChanged = new EventEmitter();

  changeCallback: Function = () => null;

  @Input()
  set min(min: any) {
    this.minValue = this._getAsNumber(min, this.DEFAULT_MIN);
  }

  @Input()
  set max(val: any) {
    this.maxValue = this._getAsNumber(val, this.DEFAULT_MAX);
  }

  @Input()
  set initial(val: any) {
    this.initialValue = this._getAsNumber(val, this.DEFAULT_INITIAL);
  }

  @Input()
  set step(val: any) {
    this.stepAmount = this._getAsNumber(val, this.DEFAULT_STEP);
  }

  @Input()
  set value(val: any) {
    this.currentValue = this._getAsNumber(val);
  }

  constructor(private changeDetector: ChangeDetectorRef,
              private _rxEmitter: RxEmitter) {
  }

  ngOnInit(): void {
    this.numberValue = this.initialValue;
  }

  ngAfterViewInit(): void {
    this.notifyParent = true;
  }

  get numberValue(): any {
    if (this.currentValue === null) {
      return '';
    } else {
      const val = this.currentValue.toFixed(this.floatPrecision < 0 ? 0 : this.floatPrecision).split('.');
      if (val.length > 1) {
        val[1] = trimEnd(val[1], '0');
        return val[1] !== '' ? val.join('.') : val[0];
      } else {
        return val[0];
      }
    }
  }

  set numberValue(v: any) {
    if (this.numberOrEmptyString.test(v || '') || v === null) {
      let newValue: number | null = this._getAsNumber(v);
      let forceRefresh = false;
      if (newValue !== null) {
        const preRound = newValue;
        newValue = round(newValue, this.floatPrecision);
        if (preRound !== newValue) {
          forceRefresh = true;
        }
        if (newValue < this.minValue) {
          newValue = this.minValue;
          forceRefresh = true;
        } else if (newValue > this.maxValue) {
          newValue = this.maxValue;
          forceRefresh = true;
        }
      }

      this.currentValue = newValue;
      if (this.notifyParent) {
        this.changeCallback(this.currentValue);
      }

      if (forceRefresh) {
        this._forceDisplayRefresh(this.currentValue);
      }
    } else {
      this._forceDisplayRefresh(this.currentValue);
    }
  }

  public increment(event: MouseEvent | KeyboardEvent): void {
    event.preventDefault();

    if (!this.isIncrementButtonDisabled()) {
      this.numberValue = this.currentValue === null ? this.initialValue : this.currentValue + this.stepAmount;
      this.valueChanged.emit(this.numberValue);
      this._rxEmitter.send(MessageBusEvents.FontSizeZoom, this.numberValue);
    }
  }

  public decrement(event: MouseEvent | KeyboardEvent): void {
    event.preventDefault();

    if (!this.isDecrementButtonDisabled()) {
      this.numberValue = this.currentValue === null ? this.initialValue : this.currentValue - this.stepAmount;
      this.valueChanged.emit(this.numberValue);
      this._rxEmitter.send(MessageBusEvents.FontSizeZoom, this.numberValue);
    }
  }

  public isDecrementButtonDisabled(): boolean {
    if (parseInt(this.numberValue, 10) === this.minValue) {
      return true;
    }
    return false;
  }

  public isIncrementButtonDisabled(): boolean {
    if (parseInt(this.numberValue, 10) === this.maxValue) {
      return true;
    }
    return false;
  }

  private _forceDisplayRefresh(newValue): void {
    // Fixes a display bug where a number can be manually typed, but if it is already at the min/max then it won't
    // update since this.currentValue never actually changes (it is adjusted to the min/max).  It will display the wrong
    // number in the input field, but this.currentValue will have the correct number.
    this.currentValue = newValue !== null ? null : 0;
    this.changeDetector.detectChanges();
    this.currentValue = newValue;
    this.changeDetector.detectChanges();
  }

  private _getAsNumber(obj: any, defaultValue?: number | null): number | null {
    if (typeof obj === 'string' && obj.length > 0) {
      const num = Number.parseFloat(obj);
      return Number.isNaN(num) ? null : num;
    } else if (typeof obj === 'number' && !Number.isNaN(obj)) {
      return obj;
    } else {
      return typeof defaultValue !== 'undefined' ? defaultValue : null;
    }
  }
}
